import { FilterOption } from "../Components/Common/UI/columnFilterTypes";
import ColumnHeaderFilter, {
  ColumnFilterValue,
} from "../Components/Common/UI/ColumnHeaderFilter";
import { ColumnFilterConfig } from "../Components/Common/UI/columnFilterTypes";
import { ColumnSortConfig } from "../Components/Common/UI/columnSortTypes";
import { isRegistryColumnFilterActive } from "./participantFilterActive";
import {
  formatRegistryTagIds,
  parseRegistryTagIds,
} from "./participantDrawerFilters";

export type RegistryFilterMeta = {
  filter: string;
  label?: string;
  type: string;
  options?: FilterOption[];
};

export type RegistryFilterContext = {
  filters: Record<string, string>;
  filterMeta: Record<string, RegistryFilterMeta>;
  tagList: { id: string; label: string }[];
  sortColumn: string;
  sortOrder: string;
  onSort: (sortKey: string, order: "ASC" | "DESC" | "") => void;
  onApply: (patch: Record<string, string>) => void;
  onClearField: (field: string) => void;
};

export const REGISTRY_SORTABLE_COLUMNS: Record<string, ColumnSortConfig> = {
  systemId: { sortKey: "participantCode", sortType: "text" },
  status: { sortKey: "status", sortType: "text" },
  screenerCompletedDate: { sortKey: "screenerCompletedAt", sortType: "date" },
  lastParticipationDate: { sortKey: "lastStudyDate", sortType: "date" },
  createdOn: { sortKey: "createdAt", sortType: "date" },
};

const REGISTRY_COLUMN_FILTERS: Record<
  string,
  Omit<ColumnFilterConfig, "options"> & { metaKey?: string }
> = {
  systemId: { filterKey: "participantCode", type: "text" },
  status: { filterKey: "status", type: "singleSelect", metaKey: "status" },
  redactionStatus: {
    filterKey: "redactionStatus",
    type: "singleSelect",
    metaKey: "redactionStatus",
  },
  screenerCompletedDate: {
    filterKey: "screenerCompletedDate",
    type: "dateRange",
  },
  notes: { filterKey: "notesQuery", type: "text" },
  tags: { filterKey: "tagIds", type: "tagsAutocomplete" },
};

export function renderRegistryColumnHeader(
  columnKey: string,
  label: string,
  ctx: RegistryFilterContext | undefined,
) {
  if (!ctx) {
    return label;
  }

  const sortConfig = REGISTRY_SORTABLE_COLUMNS[columnKey];
  const def = REGISTRY_COLUMN_FILTERS[columnKey];

  if (!def && !sortConfig) {
    return label;
  }

  let filterConfig: ColumnFilterConfig | undefined;
  let filterValue: ColumnFilterValue | undefined;
  let onFilterApply: ((value: ColumnFilterValue) => void) | undefined;
  let onFilterClear: (() => void) | undefined;
  let filterActive = false;

  if (def) {
    const meta = def.metaKey ? ctx.filterMeta[def.metaKey] : undefined;
    filterConfig = {
      ...def,
      options: meta?.options,
    };
    filterActive = isRegistryColumnFilterActive(columnKey, ctx.filters);

    if (def.type === "dateRange") {
      filterValue = {
        from: ctx.filters.screenerCompletedFrom || "",
        to: ctx.filters.screenerCompletedTo || "",
      };
    } else if (def.type === "tagsAutocomplete") {
      filterValue = parseRegistryTagIds(ctx.filters[def.filterKey]);
    } else {
      filterValue = ctx.filters[def.filterKey] || "";
    }

    onFilterApply = (applied: ColumnFilterValue) => {
      if (def.type === "dateRange") {
        const range = applied as { from: string; to: string };
        ctx.onApply({
          screenerCompletedFrom: range.from,
          screenerCompletedTo: range.to,
        });
        return;
      }
      if (def.type === "tagsAutocomplete") {
        ctx.onApply({
          [def.filterKey]: formatRegistryTagIds(applied as string[]),
        });
        return;
      }
      ctx.onApply({ [def.filterKey]: applied as string });
    };

    onFilterClear = () => {
      if (def.type === "dateRange") {
        ctx.onClearField("screenerCompletedDate");
        return;
      }
      ctx.onClearField(def.filterKey);
    };
  }

  return (
    <ColumnHeaderFilter
      label={label}
      filterActive={filterActive}
      filterConfig={filterConfig}
      filterValue={filterValue}
      onFilterApply={onFilterApply}
      onFilterClear={onFilterClear}
      sortConfig={sortConfig}
      sortColumn={ctx.sortColumn}
      sortOrder={ctx.sortOrder}
      onSort={ctx.onSort}
      tagOptions={ctx.tagList}
    />
  );
}
